<template>
  <Modal
    data-testid="price-change-confirmation-modal"
    :model-value="modelValue"
    content-class="!overflow-y-visible !max-w-lg"
    :close-on-click-outside="false"
    :close-on-escape-key="false"
    :show-close="!isSaving"
    @update:model-value="(open) => !open && !isSaving && emit('cancel')"
  >
    <template #title>{{ t('title') }}</template>

    <p v-if="error" class="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
      {{ error }}
    </p>

    <div class="space-y-4 text-sm">
      <div
        v-if="isLongTerm && periodTotalDiffs.length"
        :class="[table(), 'grid-cols-[max-content_1fr_max-content]']"
      >
        <div :class="tableHeader()">
          <div>{{ t('periodNumber') }}</div>
          <div>{{ t('periodDates') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('periodTotal') }}</div>
        </div>
        <div
          v-for="row in periodTotalDiffs"
          :key="row.index"
          :class="[tableRow(), '!items-baseline !gap-3 !py-2']"
          data-testid="period-total-diff-row"
        >
          <div>{{ row.index + 1 }}</div>
          <div class="flex min-w-0 flex-col text-[11px] leading-tight text-obsidian-500">
            <span
              v-if="row.datesChanged && row.oldStart && row.oldEnd"
              class="line-through decoration-obsidian-400"
            >
              {{ formatPeriodRange(row.oldStart, row.oldEnd) }}
            </span>
            <span v-if="row.newStart && row.newEnd">{{
              formatPeriodRange(row.newStart, row.newEnd)
            }}</span>
            <span
              v-else-if="row.oldStart && row.oldEnd"
              class="line-through decoration-obsidian-400"
            >
              {{ formatPeriodRange(row.oldStart, row.oldEnd) }}
            </span>
          </div>
          <div :class="tableCell({ numeric: true })">
            <span v-if="row.oldTotal != null">{{ formatCurrency(row.oldTotal) }}</span>
            <span v-else class="text-obsidian-400">—</span>
            <span class="mx-1">→</span>
            <strong v-if="row.newTotal != null">{{ formatCurrency(row.newTotal) }}</strong>
            <span v-else class="text-obsidian-400">—</span>
          </div>
        </div>
      </div>

      <div class="grid w-full grid-cols-[1fr_auto_auto_auto] gap-x-2 gap-y-1">
        <template v-if="showTransferPrice">
          <span>{{ t('transferPrice') }}:</span>
          <span class="text-right">{{ formatCurrency(oldTransferPrice ?? 0) }}</span>
          <span>→</span>
          <strong class="text-right">{{ formatCurrency(newTransferPrice ?? 0) }}</strong>
        </template>
        <template v-if="isLongTerm">
          <span>{{ t('contractTotal') }}:</span>
          <span class="text-right">{{ formatCurrency(oldContractTotal) }}</span>
          <span>→</span>
          <strong class="text-right">{{ formatCurrency(newContractTotal) }}</strong>
        </template>
        <template v-else>
          <span>{{ t('newTotalPrice') }}:</span>
          <span class="text-right">{{ formatCurrency(oldContractTotal) }}</span>
          <span>→</span>
          <strong class="text-right">{{ formatCurrency(newContractTotal) }}</strong>
        </template>
        <span>{{ t('newInclusiveKm') }}:</span>
        <span class="text-right">{{ pending.booking.priceCalculation.inclusiveKm }} km</span>
        <span>→</span>
        <strong class="text-right"
          >{{ pending.dryRunBooking.priceCalculation.inclusiveKm }} km</strong
        >
      </div>

      <StatusBanner v-if="!isLongTerm && hasCancellationFee" variant="warning" :show-icon="false">
        <div class="flex w-full flex-col gap-2">
          <p class="font-medium">{{ t('cancellationFeeWarning') }}</p>
          <div class="flex items-center justify-between gap-2">
            <Checkbox v-model="applyCancellationFee">{{ t('plusCancellationFee') }}</Checkbox>
            <strong
              class="shrink-0"
              :class="{
                'font-normal text-obsidian-500 line-through': !applyCancellationFee,
              }"
              >{{ formatCurrency(cancellationFeeAmount) }}</strong
            >
          </div>
        </div>
      </StatusBanner>

      <p v-if="showExtrasResetWarning" class="text-obsidian-600">{{ t('extrasReset') }}</p>

      <Textarea
        v-if="!isLongTerm && hasCancellationFee && !applyCancellationFee"
        :input-text="feeComment"
        :label="t('feeComment')"
        :placeholder="t('feeCommentPlaceholder')"
        :is-error="!!feeCommentError"
        :hint="feeCommentError"
        @update:input-text="feeComment = $event"
      />

      <p v-if="showPaymentSection" class="text-obsidian-600">
        {{ t('paymentDescription', { amount: formatCurrency(surchargeAmount) }) }}
      </p>

      <div v-if="showCounterpartyPicker || showPaymentSection" class="grid grid-cols-1 gap-3">
        <Select
          v-if="showCounterpartyPicker"
          v-model="selectedCounterparty"
          :label="t('priceDifferenceCounterpartyTitle')"
          :options="counterpartyOptions"
        />
        <Select
          v-if="showPaymentSection"
          v-model="selectedPaymentMethodId"
          :label="t('paymentMethod')"
          :options="paymentMethodOptions"
        />
        <Input
          v-if="showPaymentSection"
          :model-value="surchargeAmount"
          :label="t('amount')"
          type="number"
          :max-fraction-digits="2"
          :min="0"
          adornment="€"
          readonly
        />
      </div>
    </div>

    <div class="mt-6 flex flex-col gap-2">
      <CVButton
        class="w-full"
        variant="default"
        :disabled="!canConfirm"
        :is-loading="isSaving"
        @click="emit('confirm', buildSubmit(false))"
      >
        {{ t('acceptNewPrice') }}
      </CVButton>
      <CVButton
        v-if="pending.allowKeepOldPrice"
        class="w-full"
        variant="secondary"
        :disabled="isSaving"
        @click="emit('confirm', buildSubmit(true))"
      >
        {{ t('keepOldPrice') }}
      </CVButton>
      <CVButton class="w-full" variant="ghost" :disabled="isSaving" @click="emit('cancel')">{{
        t('cancel')
      }}</CVButton>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Checkbox from '@/components/Checkbox.vue';
import Select from '@/components/Select.vue';
import Input from '@/components/Input.vue';
import Textarea from '@/components/Textarea.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { getTotalTransferPrice } from '@/utils/get-total-transfer-price';
import { CancellationFee, CounterpartyType, CreditCard } from '@carvia/ros-client-types';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoredPaymentMethods } from '@/queries/use-payment-api';
import { formatStoredPaymentMethodLabel } from '@/utils/format-stored-payment-method-label';
import { DateTime } from 'luxon';
import { formatDate } from '@/hooks/use-formatted-date';
import { table, tableCell, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import {
  asShortTermPricing,
  diffLongTermPeriodTotals,
  isLongTermPricing,
} from '@/utils/pricing.utils';
import { getDefaultPriceDifferenceCounterparty } from '@/utils/price-difference-counterparty';
import {
  dryRunCancellationFee,
  needsPostHandoverCharge,
  resolveCounterparty,
  showCounterpartySelect,
  quoteTotal,
  type ModalSubmit,
  type PendingConfirmation,
} from './price-change-confirmation';

const props = defineProps<{
  modelValue: boolean;
  pending: PendingConfirmation;
  isSaving: boolean;
  error: string | null;
}>();

const emit = defineEmits<{
  (e: 'confirm', submit: ModalSubmit): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const booking = computed(() => props.pending.booking);
const dryRunBooking = computed(() => props.pending.dryRunBooking);
const isLongTerm = computed(() => isLongTermPricing(booking.value.priceCalculation));
const oldContractTotal = computed(() => quoteTotal(booking.value));
const newContractTotal = computed(() => quoteTotal(dryRunBooking.value));
const periodTotalDiffs = computed(() => {
  const oldPricing = booking.value.priceCalculation;
  const newPricing = dryRunBooking.value.priceCalculation;
  return isLongTermPricing(oldPricing) && isLongTermPricing(newPricing)
    ? diffLongTermPeriodTotals(oldPricing, newPricing)
    : [];
});
const formatPeriodRange = (start: Date | string, end: Date | string) =>
  `${formatDate(start, DateTime.DATE_SHORT)} – ${formatDate(end, DateTime.DATE_SHORT)}`;
const priceDifferenceCounterparty = computed(() => props.pending.priceDifferenceCounterparty);

const selectedCounterparty = ref(getDefaultPriceDifferenceCounterparty(booking.value));
const selectedCancellationFee = ref(dryRunCancellationFee(dryRunBooking.value));
const feeComment = ref('');
const selectedPaymentMethodId = ref<string | null>(null);

const cancellationFee = computed(() => dryRunCancellationFee(dryRunBooking.value));
const cancellationFeeAmount = computed(
  () => asShortTermPricing(dryRunBooking.value.priceCalculation)?.cancellationFeeAmount ?? 0,
);
const hasCancellationFee = computed(() => cancellationFee.value !== CancellationFee.NO_FEE);
const applyCancellationFee = computed({
  get: () => selectedCancellationFee.value !== CancellationFee.NO_FEE,
  set: (apply: boolean) => {
    selectedCancellationFee.value = apply ? cancellationFee.value : CancellationFee.NO_FEE;
  },
});
const showExtrasResetWarning = computed(
  () => props.pending.updateBookingDto?.pricing?.bookedExtras === null,
);
const showCounterpartyPicker = computed(() =>
  showCounterpartySelect(booking.value, dryRunBooking.value, priceDifferenceCounterparty.value),
);
const surchargeAmount = computed(() => newContractTotal.value - oldContractTotal.value);
const showPaymentSection = computed(() =>
  needsPostHandoverCharge(
    booking.value,
    dryRunBooking.value,
    false,
    resolveCounterparty(
      booking.value,
      dryRunBooking.value,
      priceDifferenceCounterparty.value,
      selectedCounterparty.value,
    ),
  ),
);

const oldTransferPrice = computed(() => getTotalTransferPrice(booking.value));
const newTransferPrice = computed(() => getTotalTransferPrice(dryRunBooking.value));
const showTransferPrice = computed(
  () => oldTransferPrice.value != null || newTransferPrice.value != null,
);

const counterpartyOptions = computed(() => [
  { label: t('priceDifferenceAgency'), value: CounterpartyType.AGENCY },
  { label: t('priceDifferenceCustomer'), value: CounterpartyType.CUSTOMER },
]);

const { data: storedPaymentMethods } = useStoredPaymentMethods(computed(() => booking.value.id));

const paymentMethodOptions = computed(() =>
  (storedPaymentMethods.value ?? [])
    .filter(
      ({ userId, purposes }) =>
        (userId && booking.value.customer?.id === userId) || purposes.includes(CreditCard.DEPOSIT),
    )
    .map((method) => ({
      label: formatStoredPaymentMethodLabel(method),
      value: method.id,
    })),
);

const feeCommentError = computed(() =>
  hasCancellationFee.value && !applyCancellationFee.value && feeComment.value.length < 10
    ? t('commentTooShort')
    : undefined,
);

const canConfirm = computed(
  () =>
    !props.isSaving &&
    !feeCommentError.value &&
    (!showPaymentSection.value || selectedPaymentMethodId.value),
);

watch(
  () => props.pending,
  (next) => {
    selectedCounterparty.value =
      next.priceDifferenceCounterparty ?? getDefaultPriceDifferenceCounterparty(next.booking);
    selectedCancellationFee.value = dryRunCancellationFee(next.dryRunBooking);
    feeComment.value = '';
    selectedPaymentMethodId.value = null;
  },
  { immediate: true },
);

const buildSubmit = (keptOldPrice: boolean): ModalSubmit => ({
  keptOldPrice,
  priceDifferenceCounterparty: showCounterpartyPicker.value
    ? selectedCounterparty.value
    : priceDifferenceCounterparty.value,
  selectedCancellationFee: selectedCancellationFee.value,
  overrideCancellationFeeComment: applyCancellationFee.value ? undefined : feeComment.value,
  storedPaymentMethodId: showPaymentSection.value
    ? (selectedPaymentMethodId.value ?? undefined)
    : undefined,
});
</script>

<i18n lang="json">
{
  "en": {
    "title": "Price calculated",
    "acceptNewPrice": "Accept new price",
    "keepOldPrice": "Continue with old price",
    "newTotalPrice": "New total price",
    "contractTotal": "Contract total",
    "periodNumber": "No.",
    "periodDates": "Dates",
    "periodTotal": "Total",
    "transferPrice": "Transfer price",
    "newInclusiveKm": "New Incl.-Kilometers",
    "extrasReset": "With this price/km-update all extras will be switched to default.",
    "cancel": "Cancel",
    "cancellationFeeWarning": "Caution: A rebooking fee applies",
    "plusCancellationFee": "Plus rebooking fee",
    "feeComment": "More infos about selection:",
    "feeCommentPlaceholder": "More reasons and details on the reduction of the fee",
    "commentTooShort": "Comment too short",
    "priceDifferenceCounterpartyTitle": "Who pays the price difference?",
    "priceDifferenceAgency": "Agency",
    "priceDifferenceCustomer": "Customer",
    "paymentMethod": "Payment method",
    "amount": "Amount",
    "paymentDescription": "The booking is already handed over. The price increase of {amount} will be charged immediately to the selected payment method."
  },
  "de": {
    "title": "Preis berechnet",
    "acceptNewPrice": "Neuen Preis akzeptieren",
    "keepOldPrice": "Weiter mit altem Preis",
    "newTotalPrice": "Neuer Gesamtpreis",
    "contractTotal": "Vertragssumme",
    "periodNumber": "Nr.",
    "periodDates": "Zeitraum",
    "periodTotal": "Preis",
    "transferPrice": "Transferpreis",
    "newInclusiveKm": "Neue Inkl.-Kilometer",
    "extrasReset": "Mit dem Preis-/Kilometer-Update werden alle Extras zurückgesetzt.",
    "cancel": "Abbrechen",
    "cancellationFeeWarning": "Vorsicht: Umbuchungsgebühr wird fällig",
    "plusCancellationFee": "Zzgl. Umbuchungsgebühr",
    "feeComment": "Mehr Infos zur Auswahl:",
    "feeCommentPlaceholder": "Weitere Gründe und Details zur Herabsetzung der Gebühr",
    "commentTooShort": "Kommentar zu kurz",
    "priceDifferenceCounterpartyTitle": "Wer trägt die Preisdifferenz?",
    "priceDifferenceAgency": "Agentur",
    "priceDifferenceCustomer": "Kunde",
    "paymentMethod": "Zahlungsart",
    "amount": "Betrag",
    "paymentDescription": "Die Buchung ist bereits übergeben. Die Preiserhöhung von {amount} wird sofort über die ausgewählte Zahlungsart belastet."
  },
  "es": {
    "title": "Precio calculado",
    "acceptNewPrice": "Aceptar nuevo precio",
    "keepOldPrice": "Continuar con precio anterior",
    "newTotalPrice": "Nuevo precio total",
    "contractTotal": "Total del contrato",
    "periodNumber": "N.º",
    "periodDates": "Fechas",
    "periodTotal": "Total",
    "transferPrice": "Precio de transferencia",
    "newInclusiveKm": "Km incluidos nuevos",
    "extrasReset": "Con esta actualización de precio/km, todos los extras volverán al valor predeterminado.",
    "cancel": "Cancelar",
    "cancellationFeeWarning": "Atención: Se aplica una tarifa de cambio",
    "plusCancellationFee": "Más tarifa de cambio",
    "feeComment": "Más información sobre la selección:",
    "feeCommentPlaceholder": "Más motivos y detalles sobre la reducción de la tarifa",
    "commentTooShort": "Comentario demasiado corto",
    "priceDifferenceCounterpartyTitle": "¿Quién paga la diferencia de precio?",
    "priceDifferenceAgency": "Agencia",
    "priceDifferenceCustomer": "Cliente",
    "paymentMethod": "Método de pago",
    "amount": "Importe",
    "paymentDescription": "La reserva ya está entregada. El aumento de precio de {amount} se cargará de inmediato al método de pago seleccionado."
  }
}
</i18n>
